import React, { useState, useEffect, useMemo } from "react";
import {
  Typography,
  Chip,
  Stack,
  Accordion,
  AccordionDetails,
  Button,
  Box,
} from "@mui/material";
import CustomAccordionSummary from "styles/customAccordionSummary";
import { ExpandMore } from "@mui/icons-material";
import {
  ALL,
  COVERAGE_CRITERIA_ALL,
  initialLobValues,
  validationOptions,
} from "../Constants/PolicyTranslationConstants";
import Chips from "components/Chips";
import { getLobNamesForFilter } from "../Utils/TranslationUtils";
import { useDispatch } from "react-redux";
import { setDialog } from "slices/appSlice";

/**
 * TranslationFilter
 * Shared between PolicyTranslation and UseCases
 */
function TranslationFilter({
  ccList,
  lobList,
  selectedLob,
  setSelectedLob,
  selectedCC,
  setSelectedCC,
  selectedVariance,
  setSelectedVariance,
  selectedUseCase,
  setSelectedUseCase,
  onAccordionToggle,
  showVarianceFilter,
  showUseCaseFilter,
  policyData,
  policyRules,
  selectedvalidation,
  setselectedvalidation,
}) {
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState(false);

  // ✅ Derive available CCs from policyRules (no setState in an effect)
  const availableCoverageCriteria = useMemo(() => {
    const set = new Set();
    Object.values(policyRules || {}).forEach((rulesArr) => {
      rulesArr?.forEach((rule) => {
        const facts = rule?.RuleFacts;
        const list = facts?.CoverageCriteriaList;
        if (list && list !== "None") {
          String(list)
            .split(",")
            .map((cc) => cc.trim())
            .forEach((cc) => {
              if (cc) set.add(cc);
            });
        }
      });
    });
    return Array.from(set);
  }, [policyRules]);

  // ✅ Derive LOB chips from inputs (no setState in an effect)
  const lobListValues = useMemo(() => {
    const newLobs = lobList.map((lob) => ({
      key: lob,
      value: getLobNamesForFilter(lob, policyData),
    }));
    return [...initialLobValues, ...newLobs];
  }, [policyData, lobList]);

  // This effect is OK: it syncs to an external system (Redux dialog), not local state.
  useEffect(() => {
    if (selectedCC !== ALL) {
      const hasMatching = availableCoverageCriteria.includes(selectedCC);
      if (!hasMatching) {
        dispatch(
          setDialog({
            message:
              "Policy Rules have not been created for the selected coverage criteria.  Select the appropriate rule tab and click Add Procedure Code to configure the appropriate rule(s).",
          })
        );
      }
    }
  }, [selectedCC, availableCoverageCriteria, dispatch]);

  const varianceValues = [
    { label: ALL, selected: false, value: ALL },
    { label: "Variance", selected: false, value: "VARIANCE" },
  ];

  const useCaseValues = [
    { label: ALL, selected: false, value: ALL },
    { label: "To Do", selected: false, value: "ToDo" },
    { label: "Complete", selected: false, value: "Complete" },
  ];

  const handleAccordionChange = () => {
    setExpanded((prev) => {
      const next = !prev;
      onAccordionToggle();
      return next;
    });
  };

  const handleResetFilters = () => {
    setSelectedCC(ALL);
    setSelectedVariance(ALL);
    setSelectedUseCase(ALL);
    setSelectedLob(ALL);
  };

  const isResetVisible =
    selectedCC !== ALL ||
    selectedVariance !== ALL ||
    selectedUseCase !== ALL ||
    selectedLob !== ALL;

  return (
    <Accordion
      sx={{ padding: 0, margin: 0 }}
      expanded={expanded}
      onChange={handleAccordionChange}
    >
      <CustomAccordionSummary
        sx={{ padding: 0, margin: 0 }}
        expandIcon={<ExpandMore />}
        aria-controls="panel1a-content"
        id="panel1a-header"
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ padding: 0, mb: 0 }}
          px={(theme) => theme.spacing(1)}
        >
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            Quick Filters:
          </Typography>
          {isResetVisible && (
            <Button onClick={handleResetFilters} color={"chip"}>
              Reset
            </Button>
          )}
          {!expanded && selectedCC !== ALL && (
            <Typography variant="body2">
              <span style={{ fontWeight: "bold" }}>By Coverage Criteria:</span>{" "}
              {selectedCC}
            </Typography>
          )}
          {!expanded && showVarianceFilter && selectedVariance !== ALL && (
            <Typography variant="body2">
              <span style={{ fontWeight: "bold" }}>By Variance:</span>{" "}
              {selectedVariance}
            </Typography>
          )}
          {!expanded && showUseCaseFilter && selectedUseCase !== ALL && (
            <Typography variant="body2">
              <span style={{ fontWeight: "bold" }}>By Use Case:</span>{" "}
              {selectedUseCase}
            </Typography>
          )}
        </Stack>
      </CustomAccordionSummary>

      <AccordionDetails sx={{ padding: 0, mb: 1 }}>
        <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: "wrap" }}>
          <Typography
            variant="subtitle1"
            sx={{ minWidth: 170, fontWeight: "bold" }}
          >
            By Coverage Criteria:
          </Typography>
          {[COVERAGE_CRITERIA_ALL, ...ccList].map((cc) => (
            <Chip
              sx={{ minWidth: 100 }}
              key={cc}
              label={cc}
              onClick={() => setSelectedCC(cc)}
              color={selectedCC === cc ? "selectedChip" : "chip"}
            />
          ))}
        </Stack>

        {showVarianceFilter && (
          <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: "wrap" }}>
            <Typography
              variant="subtitle1"
              sx={{ minWidth: 170, fontWeight: "bold" }}
            >
              By Variance:
            </Typography>
            <Chips
              values={varianceValues}
              activeFilter={selectedVariance}
              selectedFilter={(data) => setSelectedVariance(data.value)}
            />
          </Stack>
        )}

        {showUseCaseFilter && (
          <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: "wrap" }}>
            <Typography
              variant="subtitle1"
              sx={{ minWidth: 170, fontWeight: "bold" }}
            >
              By Use Case:
            </Typography>
            <Chips
              values={useCaseValues}
              activeFilter={selectedUseCase}
              selectedFilter={(data) => setSelectedUseCase(data.value)}
            />
          </Stack>
        )}

        <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: "wrap" }}>
          <Typography
            variant="subtitle1"
            sx={{ minWidth: 170, fontWeight: "bold" }}
          >
            By LOB:
          </Typography>
          {lobListValues.map((lob) => (
            <Chip
              sx={{ minWidth: 100 }}
              key={lob.key}
              label={lob.value}
              onClick={() => setSelectedLob(lob.key)}
              color={selectedLob === lob.key ? "selectedChip" : "chip"}
            />
          ))}
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: "wrap" }}>
          <Typography
            variant="subtitle1"
            sx={{ minWidth: 170, fontWeight: "bold" }}
          >
            By Validation:
          </Typography>
          {validationOptions.map((op) => (
            <Chip
              sx={{ minWidth: 100 }}
              key={op.value}
              label={op.label}
              onClick={() => setselectedvalidation(op.value)}
              color={selectedvalidation === op.value ? "selectedChip" : "chip"}
            />
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

function ProcedureCodeDialogFilter({
  chips,
  handleFilterCodeList,
  dialogSelectedCC,
  selectedVarianceOption,
  setSelectedVarianceOption,
  handleFilterVarianceList,
  setDialogSelectedCC,
}) {
  const varianceValues = [
    { label: ALL, selected: false, value: ALL },
    { label: "Variance", selected: false, value: ["NEW", "VARIANCE"] },
    { label: "No Variance", selected: false, value: "NOVARIANCE" },
  ];
  const handleCCFilterClick = (cc) => {
    setDialogSelectedCC(cc);
    handleFilterCodeList(cc);
  };
  const handleVarianceFilterClick = (v) => {
    setSelectedVarianceOption(Array.isArray(v) ? v[0] : v);
    handleFilterVarianceList(v);
  };
  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 0.5 }}>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}
        >
          By CC#
        </Typography>
        {[COVERAGE_CRITERIA_ALL, ...chips].map((cc) => (
          <Chip
            sx={{ minWidth: 50 }}
            key={cc}
            label={cc}
            onClick={() => handleCCFilterClick(cc)}
            color={dialogSelectedCC === cc ? "selectedChip" : "chip"}
          />
        ))}
      </Stack>
      <Stack
        direction="row"
        spacing={1}
        sx={{ flexWrap: "wrap", gap: 0.5, marginTop: 1 }}
      >
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: "bold", whiteSpace: "nowrap", marginTop: "1px" }}
        >
          By Variance:
        </Typography>

        {varianceValues.map((v) => (
          <Chip
            sx={{ minWidth: 50 }}
            key={Array.isArray(v.value) ? v.value.join("-") : v.value}
            label={v.label}
            onClick={() => handleVarianceFilterClick(v.value)}
            color={
              Array.isArray(v.value)
                ? v.value.includes(selectedVarianceOption)
                  ? "selectedChip"
                  : "chip"
                : selectedVarianceOption === v.value
                  ? "selectedChip"
                  : "chip"
            }
          />
        ))}
      </Stack>
    </Box>
  );
}

export { TranslationFilter, ProcedureCodeDialogFilter };
